// VenueDashboardHangoutsPage.jsx — VEN-1449 OFFERS×HANGOUTS: the venue as a participant.
// Owner/manager sees upcoming hangouts happening AT their venue and attaches one of
// their active offers ("group of 5+ tonight → first round free"). The sponsorship
// badge then rides the consumer hangout card/detail — a reason for hosts to
// venue-anchor and for groups to show up. Redemption uses the existing offers/QR rails.
// API: GET /api/me/venues/:id/hangouts · POST/DELETE /api/me/venues/:id/hangouts/:hid/offer
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl } from '../../config/region';
import DashboardNavBar from './DashboardNavBar';
import { dtLocale } from '../../utils/dateLocale';

const ORANGE = '#E46E34';

function token() {
  return (
    localStorage.getItem('venueapp_token') ||
    localStorage.getItem('venue_app_token') ||
    localStorage.getItem('token') || ''
  );
}
const authHeaders = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` });
const fmtDT = (iso) => new Date(iso).toLocaleString(dtLocale(), { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

export default function VenueDashboardHangoutsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { slug } = useParams();
  const base = getApiBaseUrl();

  const [venue, setVenue] = useState(null);
  const [hangouts, setHangouts] = useState([]);
  const [offers, setOffers] = useState([]);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pickerFor, setPickerFor] = useState(null); // hangout id with the offer picker open

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const vRes = await fetch(`${base}/venue-dashboard/${encodeURIComponent(slug)}`, { headers: authHeaders() });
      const vj = await vRes.json();
      const v = vj?.data || vj?.venue || vj;
      if (!v?.id) throw new Error(vj?.message || t('vd.venueNotFoundNoAccess'));
      setVenue({ id: v.id, name: v.venue_name || v.name || slug });
      const hRes = await fetch(`${base}/me/venues/${v.id}/hangouts`, { headers: authHeaders() });
      const hj = await hRes.json();
      if (!hj?.success) throw new Error(hj?.error || t('vd.hgCouldNotLoad'));
      setHangouts(hj.hangouts || []);
      setOffers(hj.offers || []);
      setRole(hj.role);
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  }, [base, slug, t]);
  useEffect(() => { load(); }, [load]);

  const canSponsor = ['owner', 'manager'].includes(role);

  const attach = async (hangoutId, offerId) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await fetch(`${base}/me/venues/${venue.id}/hangouts/${hangoutId}/offer`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ offer_id: offerId }),
      });
      const d = await r.json();
      if (!d.success) throw new Error(d.error || t('vd.hgAttachFailed'));
      setPickerFor(null);
      await load();
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  };

  const detach = async (hangoutId) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await fetch(`${base}/me/venues/${venue.id}/hangouts/${hangoutId}/offer`, {
        method: 'DELETE', headers: authHeaders(),
      });
      const d = await r.json();
      if (!d.success) throw new Error(d.error || t('vd.hgRemoveFailed'));
      await load();
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#fafafa' }}>
      <div style={{ position: 'sticky', top: 0, zIndex: 20, background: '#fff', borderBottom: '1px solid #eee', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <button onClick={() => navigate(`/venue-dashboard/${slug}`)} aria-label={t('vd.back')} style={{ width: 36, height: 36, borderRadius: 18, border: 'none', background: '#f3f4f6', fontSize: 18, cursor: 'pointer' }}>←</button>
        <div>
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#111827' }}>🤝 {t('vd.hgHeaderTitle')}</h1>
          <div style={{ fontSize: 11, color: '#9ca3af' }}>{t('vd.hgHeaderSubtitle')}</div>
        </div>
      </div>

      <div style={{ padding: '14px', paddingBottom: 90 }}>
        {loading ? (
          <div style={{ textAlign: 'center', color: '#6b7280', padding: 40 }}>{t('vd.loading')}</div>
        ) : err ? (
          <div style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 10, padding: '10px 14px', fontSize: 13 }}>{err}</div>
        ) : hangouts.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#6b7280', padding: 40 }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>🤝</div>
            <div style={{ fontWeight: 700, color: '#374151' }}>{t('vd.hgNoUpcomingYet')}</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>{t('vd.hgWhenSomeonePlans', { name: venue?.name })}</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {hangouts.map((h) => (
              <div key={h.id} style={{ background: '#fff', border: '1px solid #eee', borderRadius: 14, padding: '12px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 800, color: ORANGE }}>{fmtDT(h.starts_at)}</span>
                  <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{h.title}</span>
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                  {t('vd.hgHostLine', { host: h.host_name, count: h.going_count })}{h.maybe_count ? ` · ${t('vd.hgMaybeCount', { count: h.maybe_count })}` : ''}
                </div>

                {h.offer_id ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, background: '#fff7ed', border: '1px solid #fde4d3', borderRadius: 10, padding: '8px 12px' }}>
                    <span style={{ fontSize: 16 }}>🎁</span>
                    <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: '#111827' }}>
                      {h.offer_title}
                      {h.offer_group_min_size ? <span style={{ color: '#6b7280', fontWeight: 500 }}> · {t('vd.hgGroupsOf', { count: h.offer_group_min_size })}</span> : null}
                    </span>
                    {canSponsor && (
                      <button onClick={() => detach(h.id)} disabled={busy} style={{ border: 'none', background: 'transparent', color: '#9ca3af', fontSize: 15, cursor: 'pointer' }} title={t('vd.hgRemoveSponsorship')}>✕</button>
                    )}
                  </div>
                ) : canSponsor ? (
                  pickerFor === h.id ? (
                    <div style={{ marginTop: 10 }}>
                      {offers.length === 0 ? (
                        <div style={{ fontSize: 12, color: '#6b7280' }}>
                          {t('vd.hgNoActiveOffersYet')} — <button onClick={() => navigate(`/venue-dashboard/${slug}/offers`)} style={{ border: 'none', background: 'none', color: ORANGE, fontWeight: 700, cursor: 'pointer', padding: 0, fontSize: 12 }}>{t('vd.hgCreateOneFirst')} →</button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {offers.map((o) => (
                            <button key={o.id} onClick={() => attach(h.id, o.id)} disabled={busy}
                              style={{ display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left', border: '1px solid #fde4d3', background: '#fff', borderRadius: 10, padding: '8px 12px', cursor: 'pointer', fontSize: 13 }}>
                              🎁 <span style={{ fontWeight: 700, color: '#111827' }}>{o.title}</span>
                              {o.group_min_size ? <span style={{ color: '#6b7280', fontSize: 12 }}>{t('vd.hgGroups', { count: o.group_min_size })}</span> : null}
                            </button>
                          ))}
                          <button onClick={() => setPickerFor(null)} style={{ border: 'none', background: 'none', color: '#9ca3af', fontSize: 12, cursor: 'pointer', alignSelf: 'flex-start' }}>{t('vd.cancel')}</button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <button onClick={() => setPickerFor(h.id)} style={{ marginTop: 10, border: `1.5px dashed ${ORANGE}`, background: '#fff', color: ORANGE, fontWeight: 700, fontSize: 12, padding: '8px 14px', borderRadius: 10, cursor: 'pointer' }}>
                      + {t('vd.hgSponsorWithOffer')}
                    </button>
                  )
                ) : (
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 8 }}>{t('vd.hgOwnerManagerCanAttach')}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <DashboardNavBar />
    </div>
  );
}
